'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { FeaturedSection } from './featured-section';
import { AgentDiscoverCard } from './agent-discover-card';
import { ChannelDiscoverCard } from './channel-discover-card';
import { UserDiscoverCard } from './user-discover-card';
import { PostDiscoverCard } from './post-discover-card';
import { formatCount, cn } from '@/lib/utils';
import { Avatar } from '@/components/avatar';
import { useTranslation } from '@/components/i18n-provider';
import type { FeaturedData, ContentType, ViewMode } from './use-discover';

interface DiscoverFeaturedProps {
  data: FeaturedData | null;
  loading: boolean;
  error?: boolean;
  onRetry?: () => void;
  viewMode: ViewMode;
  onSeeAll: (type: ContentType) => void;
  onToggleTag?: (tag: string) => void;
  onLoadMore?: (section: string) => void;
  sectionLoading?: Record<string, boolean>;
}

function FeaturedSkeleton() {
  return (
    <div className="flex gap-3">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="w-[80vw] max-w-80 shrink-0 rounded-xl border border-border p-4 animate-pulse">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl bg-muted shrink-0" />
            <div className="flex-1">
              <div className="h-4 bg-muted rounded w-3/4 mb-2" />
              <div className="h-3 bg-muted rounded w-1/2" />
            </div>
          </div>
          <div className="h-3 bg-muted rounded w-full mt-3" />
          <div className="h-3 bg-muted rounded w-2/3 mt-1.5" />
        </div>
      ))}
    </div>
  );
}

function ChannelWithSubs({ channel, viewMode }: { channel: any; viewMode: ViewMode }) {
  const { t } = useTranslation();
  const subs: any[] = channel.subchannels || [];
  const isList = viewMode === 'list';

  if (subs.length === 0) {
    return (
      <div className={cn(isList ? '' : 'w-[80vw] max-w-80 shrink-0')}>
        <ChannelDiscoverCard channel={channel} viewMode={viewMode} />
      </div>
    );
  }

  return (
    <div className={cn(
      'rounded-xl border border-border/60 overflow-hidden bg-card',
      isList ? '' : 'w-[80vw] max-w-80 shrink-0'
    )}>
      <Link href={`/c/${channel.slug}`} className="flex items-center gap-2.5 px-3.5 py-3 hover:bg-accent/40 transition-colors">
        <Avatar seed={channel.slug} type="channel" src={channel.logoUrl} className="h-9 w-9 shrink-0" />
        <div className="min-w-0 flex-1">
          <span className="text-sm font-semibold truncate block">{channel.name}</span>
          <span className="text-[11px] text-muted-foreground">{formatCount(channel.memberCount || 0)} {t('discover.members')} · {formatCount(channel.postCount || 0)} {t('discover.posts')}</span>
        </div>
      </Link>
      <div className="border-t border-border/30 px-2 py-1.5 space-y-0.5">
        {subs.slice(0, 4).map((sub: any) => (
            <Link key={sub.id} href={`/c/${sub.slug}`} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent/40 transition-colors">
              <Avatar seed={sub.slug} type="channel" src={sub.logoUrl} className="h-5 w-5 shrink-0" />
              <span className="text-xs font-medium truncate flex-1">{sub.name}</span>
              <span className="text-[10px] text-muted-foreground shrink-0">{formatCount(sub.postCount || 0)}</span>
            </Link>
          ))}
      </div>
    </div>
  );
}

export function DiscoverFeatured({ data, loading, error, onRetry, viewMode, onSeeAll, onToggleTag, onLoadMore, sectionLoading }: DiscoverFeaturedProps) {
  const { t } = useTranslation();

  // Trending widgets — fetched independently of /api/featured/trending
  // because that endpoint doesn't expose a widget section yet. We
  // piggyback on /api/posts with `content_type=widget&sort=hot`, which
  // returns the seed-system-widgets entries (Calculator, Python REPL,
  // Snake, …) alongside any user-published HTML widgets. Sits between
  // Agents and Posts so the marketplace gets headline real-estate.
  const [widgets, setWidgets] = useState<any[] | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    fetch('/api/posts?content_type=widget&sort=hot&limit=12', { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { posts?: any[] } | null) => {
        if (ctrl.signal.aborted) return;
        setWidgets(d?.posts ?? []);
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  if (!loading && error && !data) {
    return (
      <div className="text-center py-16 space-y-3">
        <p className="text-muted-foreground">{t('discover.featuredError')}</p>
        {onRetry && (
          <button
            onClick={() => onRetry()}
            className="text-sm font-medium text-primary hover:underline"
          >
            {t('errors.tryAgain')}
          </button>
        )}
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="space-y-8">
        {['Agents', 'Channels', 'Posts', 'Humans', 'Tags'].map(label => (
          <div key={label} className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="h-4 bg-muted rounded w-32 animate-pulse" />
              <div className="h-3 bg-muted rounded w-16 animate-pulse" />
            </div>
            <FeaturedSkeleton />
          </div>
        ))}
      </div>
    );
  }

  const hasAny = (data.agents?.items.length || 0) + (data.channels?.items.length || 0) +
    (data.users?.items.length || 0) + (data.posts?.items.length || 0) +
    (data.tags?.items.length || 0) > 0;

  if (!hasAny) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t('discover.featuredEmpty')}</p>
      </div>
    );
  }

  const isList = viewMode === 'list';

  return (
    <div className="space-y-8">
      {(data.agents?.items.length || 0) > 0 && (
        <FeaturedSection
          id="agents"
          title={t('discover.agents')}
          viewMode={viewMode}
          onSeeAll={() => onSeeAll('agents')}
          onLoadMore={onLoadMore ? () => onLoadMore('agents') : undefined}
          loadingMore={sectionLoading?.agents}
        >
          {data.agents!.items.map((agent: any) => (
            <div key={agent.id} className={isList ? '' : 'w-[80vw] max-w-80 shrink-0'}>
              <AgentDiscoverCard agent={agent} viewMode={viewMode} />
            </div>
          ))}
        </FeaturedSection>
      )}

      {widgets && widgets.length > 0 && (
        <FeaturedSection
          id="widgets"
          title={t('discover.widgets')}
          viewMode={viewMode}
          onSeeAll={() => onSeeAll('widgets')}
        >
          {widgets.map((post: any) => (
            <div key={post.id} className={isList ? '' : 'w-[80vw] max-w-80 shrink-0'}>
              <PostDiscoverCard post={post} viewMode={viewMode} />
            </div>
          ))}
        </FeaturedSection>
      )}

      {(data.channels?.items.length || 0) > 0 && (
        <FeaturedSection
          id="channels"
          title={t('discover.marketplaceChannels')}
          viewMode={viewMode}
          onSeeAll={() => onSeeAll('channels')}
          onLoadMore={onLoadMore ? () => onLoadMore('channels') : undefined}
          loadingMore={sectionLoading?.channels}
        >
          {data.channels!.items.map((channel: any) => (
            <ChannelWithSubs key={channel.id} channel={channel} viewMode={viewMode} />
          ))}
        </FeaturedSection>
      )}

      {(data.posts?.items.length || 0) > 0 && (
        <FeaturedSection
          id="posts"
          title={t('discover.marketplacePosts')}
          viewMode={viewMode}
          onSeeAll={() => onSeeAll('posts')}
          onLoadMore={onLoadMore ? () => onLoadMore('posts') : undefined}
          loadingMore={sectionLoading?.posts}
        >
          {data.posts!.items.map((post: any) => (
            <div key={post.id} className={isList ? '' : 'w-[80vw] max-w-80 shrink-0'}>
              <PostDiscoverCard post={post} viewMode={viewMode} />
            </div>
          ))}
        </FeaturedSection>
      )}

      {/* Humans carousel hidden for now
      {(data.users?.items.length || 0) > 0 && (
        <FeaturedSection
          id="users"
          title="Humans"
          viewMode={viewMode}
          onSeeAll={() => onSeeAll('humans')}
          onLoadMore={onLoadMore ? () => onLoadMore('users') : undefined}
          loadingMore={sectionLoading?.users}
        >
          {data.users!.items.map((u: any) => (
            <div key={u.id} className={isList ? '' : 'w-[80vw] max-w-80 shrink-0'}>
              <UserDiscoverCard user={u} viewMode={viewMode} />
            </div>
          ))}
        </FeaturedSection>
      )}
      */}

      {/* Trending tags carousel hidden for now
      {(data.tags?.items.length || 0) > 0 && (
        <FeaturedSection
          id="tags"
          title={t('discover.trendingTags')}
          viewMode={viewMode}
          onSeeAll={() => onSeeAll('tags')}
          onLoadMore={onLoadMore ? () => onLoadMore('tags') : undefined}
          loadingMore={sectionLoading?.tags}
        >
          {data.tags!.items.map((tag: any) => (
            <button
              key={tag.id}
              onClick={() => onToggleTag?.(tag.name)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-accent/60 hover:bg-accent text-foreground transition-colors shrink-0"
            >
              <span className="text-muted-foreground">#</span>{tag.name}
              <span className="text-[10px] text-muted-foreground">{formatCount(tag.useCount || 0)}</span>
            </button>
          ))}
        </FeaturedSection>
      )}
      */}
    </div>
  );
}
